import { useState, useEffect, memo, useCallback } from "react";
import Swal from "sweetalert2";
import { CSVLink } from "react-csv";
import AnimacionCarga from "./animacion_de_carga";
import TablaGenerica from "./tablas";
import BotonSOS from "./sos/boton_sos";
import { subirArchivoFirebase, borrarArchivoFirebaseUrl } from "../../functions/archivos_firebase";
import { consultarSimple, crearRegistro, actualizarRegistro, eliminarRegistro } from "../../functions/crud";
import { obtenerNits, CONFIG } from "../../functions/variables_globales";
import { dialogo, escapar, mostrarEspera, mostrarError } from "./dialogos_cartera";
import { menuOpciones } from "./dialogos_carga_masiva";
import { botonOpciones } from "./listado_comun";
import "bootstrap-icons/font/bootstrap-icons.css";

// Documentos del conjunto (PDF). Misma ruta y campos que la app:
// coordi_ph/<nit>/documentos/<id> = { titulo, descripcion, url }; el PDF va en Storage fichas/ficha_<marca>.pdf.
// url también puede ser un enlace pegado a mano (Drive, página del conjunto, etc.).

// Igual que normalizarEnlace en la app: quita espacios y, si falta, agrega https://.
const normalizarEnlace = (texto = "") => {
  const limpio = String(texto).trim();
  if (!limpio) return "";
  return /^https?:\/\//i.test(limpio) ? limpio : `https://${limpio}`;
};

const enlaceValido = (url) => /^https?:\/\/[^\s/]+\.[^\s]+$/i.test(url);

const esArchivoPropio = (url) => /firebasestorage\.googleapis\.com/i.test(url || "");

const rutaColeccion = (nit) => `coordi_ph/${nit}/documentos`;
const rutaDocumento = (nit, id) => `${rutaColeccion(nit)}/${id}`;

const puedeGestionar = () => ["ADMINISTRADOR", "SUPER"].includes(localStorage.getItem("Permisos"));

const COLUMNAS = [
  { label: "Título", key: "titulo" },
  { label: "Descripción", key: "descripcion" },
  { label: "Archivo", key: "archivo" },
];

const COLUMNAS_SUPER = [{ label: "Copropiedad", key: "copropiedad" }, ...COLUMNAS];

const estiloCSV = { padding: "7px 14px", margin: 2, backgroundColor: "#c0392b", color: "#fff", borderRadius: 7, fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" };

const obtenerCopropiedades = async () => {
  if (!CONFIG.esSuper()) {
    const nit = CONFIG.codigoCliente();
    return [{ nit, nombre: CONFIG.razonSocial() || nit }];
  }
  const nits = await obtenerNits();
  const clientes = await consultarSimple("/clientes");
  const nombres = {};
  await Promise.all(
    Object.entries(clientes || {}).map(async ([codigo, id]) => {
      const datos = await consultarSimple(`/coordi_ph_clientes/${codigo}/${id}`);
      nombres[codigo] = datos?.Nombre || datos?.Nit || codigo;
    })
  );
  return nits
    .map((nit) => ({ nit, nombre: nombres[nit] || nit }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
};

const obtenerDocumentos = async (copropiedades) => {
  const porNit = await Promise.all(
    copropiedades.map(async ({ nit, nombre }) => {
      const datos = await consultarSimple(rutaColeccion(nit));
      return Object.entries(datos || {})
        .filter(([, doc]) => doc && typeof doc === "object")
        .map(([id, doc]) => ({
          id,
          nit,
          copropiedad: nombre,
          titulo: doc.titulo || "Sin título",
          descripcion: doc.descripcion || "",
          url: doc.url || "",
        }));
    })
  );
  return porNit.flat().sort((a, b) => a.titulo.localeCompare(b.titulo, "es"));
};

// ---------- Ventanas ----------

const ESTILO_FORMULARIO = `
  <style>
    .cartera-popup #doc-form { text-align:left; margin-top:6px; }
    .cartera-popup #doc-form label { display:block; margin:14px 0 6px; color:#3a3a3c; font-size:15px; font-weight:600; }
    .cartera-popup #doc-form input[type=text], .cartera-popup #doc-form textarea, .cartera-popup #doc-form select {
      width:100%; box-sizing:border-box; padding:12px 14px; border:1px solid #d1d1d6; border-radius:12px;
      font-size:16px; font-family:inherit; background:#fff; color:#1c1c1e; margin:0; }
    .cartera-popup #doc-form textarea { min-height:84px; resize:vertical; }
    .cartera-popup #doc-form input:focus, .cartera-popup #doc-form textarea:focus, .cartera-popup #doc-form select:focus {
      outline:none; border-color:#c0392b; box-shadow:0 0 0 3px rgba(192,57,43,.15); }
    .cartera-popup #doc-form .doc-archivo { display:flex; align-items:center; gap:12px; padding:14px; border:1.5px dashed #c7c7cc;
      border-radius:12px; background:#f9f9fb; cursor:pointer; margin:0; font-weight:500; font-size:15px; color:#1c1c1e; }
    .cartera-popup #doc-form .doc-archivo:hover { border-color:#c0392b; }
    .cartera-popup #doc-form .doc-archivo.listo { border-style:solid; border-color:#34c759; background:#f0fbf3; }
    .cartera-popup #doc-form .doc-archivo i { font-size:26px; color:#c0392b; }
    .cartera-popup #doc-form .doc-archivo.listo i { color:#34c759; }
    .cartera-popup #doc-form .doc-ayuda { font-size:13px; color:#6e6e73; margin:6px 2px 0; }
    .cartera-popup #doc-form .doc-columnas { display:grid; grid-template-columns:1fr 1fr; gap:0 22px; }
    @media (max-width: 700px) { .cartera-popup #doc-form .doc-columnas { grid-template-columns:1fr; } }
  </style>`;

const formularioHTML = (doc, copropiedades) => {
  const elegirCopropiedad = !doc.id && copropiedades.length > 1;
  return `${ESTILO_FORMULARIO}
  <div id="doc-form">
    ${elegirCopropiedad ? `
      <label for="docNit">Copropiedad</label>
      <select id="docNit">
        <option value="">Seleccione la copropiedad</option>
        ${copropiedades.map((c) => `<option value="${escapar(c.nit)}">${escapar(c.nombre)}</option>`).join("")}
      </select>` : ""}
    <div class="doc-columnas">
      <div>
        <label for="docTitulo">Título</label>
        <input id="docTitulo" type="text" maxlength="120" value="${escapar(doc.titulo)}" placeholder="Agregue un título para su documento.">
        <label for="docDescripcion">Descripción</label>
        <textarea id="docDescripcion" placeholder="Agregue una descripción para el documento.">${escapar(doc.descripcion)}</textarea>
      </div>
      <div>
        <label>Archivo PDF</label>
        <label for="docArchivo" class="doc-archivo" id="docArchivoCaja">
          <i class="bi bi-file-earmark-pdf"></i>
          <span id="docArchivoNombre">${doc.url ? "Cambiar el archivo PDF" : "Seleccionar archivo PDF"}</span>
        </label>
        <input id="docArchivo" type="file" accept="application/pdf" style="display:none">
        <p class="doc-ayuda">Toque el recuadro y busque el archivo PDF en su equipo.</p>
        <label for="docEnlace">O pegue el enlace del PDF</label>
        <input id="docEnlace" type="text" inputmode="url" autocomplete="off" value="${escapar(doc.url)}" placeholder="https://...">
        <p class="doc-ayuda">${doc.url
          ? "Este es el enlace actual. Puede cambiarlo, o subir otro archivo arriba."
          : "Si sube un archivo, no hace falta el enlace."}</p>
      </div>
    </div>
  </div>`;
};

const rechazar = (mensaje) => {
  Swal.showValidationMessage(mensaje);
  return false;
};

const leerFormulario = (esEdicion, elegirCopropiedad) => {
  const nit = elegirCopropiedad ? document.getElementById("docNit")?.value : null;
  const titulo = document.getElementById("docTitulo")?.value.trim() || "";
  const descripcion = document.getElementById("docDescripcion")?.value.trim() || "";
  const archivo = document.getElementById("docArchivo")?.files?.[0] || null;
  const enlace = normalizarEnlace(document.getElementById("docEnlace")?.value);

  if (elegirCopropiedad && !nit) return rechazar("Seleccione la copropiedad del documento.");
  if (!titulo) return rechazar("Debe agregar un título para poder subir el archivo.");
  if (!descripcion) return rechazar("Debe agregar una descripción para poder subir el archivo.");
  if (!archivo && !enlace) return rechazar("Suba el archivo PDF o pegue el enlace del PDF.");
  if (!archivo && !enlaceValido(enlace)) return rechazar("El enlace no es válido. Debe ser parecido a https://sitio.com/documento.pdf");
  if (archivo && archivo.type !== "application/pdf" && !/\.pdf$/i.test(archivo.name)) {
    return rechazar("El archivo debe estar en formato PDF.");
  }
  return { nit, titulo, descripcion, archivo, enlace };
};

const verPdf = (documento) => {
  if (!documento.url) {
    dialogo({ icono: "bi-file-earmark-x", tono: "naranja", titulo: "Sin archivo", texto: "Este documento no tiene un PDF cargado." });
    return;
  }
  // En celular el navegador no muestra PDF dentro de la ventana, y muchas páginas externas
  // no se dejan mostrar dentro de otra: en esos casos se abre directo.
  if (window.innerWidth < 700 || !esArchivoPropio(documento.url)) {
    window.open(documento.url, "_blank", "noopener,noreferrer");
    return;
  }
  dialogo({
    icono: "bi-file-earmark-pdf",
    titulo: escapar(documento.titulo),
    texto: escapar(documento.descripcion),
    amplio: true,
    cerrar: true,
    contenido: `<iframe src="${escapar(documento.url)}" title="${escapar(documento.titulo)}"
      style="width:100%;height:62vh;border:1px solid #e5e5ea;border-radius:12px;margin-top:12px;background:#f2f2f7;"></iframe>`,
    confirmar: "Abrir en otra pestaña",
    iconoConfirmar: "bi-box-arrow-up-right",
    cancelar: "Cerrar",
  }).then(({ isConfirmed }) => {
    if (isConfirmed) window.open(documento.url, "_blank", "noopener,noreferrer");
  });
};

const subirPdf = (archivo) => {
  const marca = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  return subirArchivoFirebase(`fichas/ficha_${marca}.pdf`, archivo);
};

const ListadoDocumentos = memo(() => {
  const [documentos, setDocumentos] = useState([]);
  const [copropiedades, setCopropiedades] = useState([]);
  const [cargando, setCargando] = useState(true);

  const esSuper = CONFIG.esSuper();
  const gestionar = puedeGestionar();

  const capturarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const lista = await obtenerCopropiedades();
      setCopropiedades(lista);
      setDocumentos(await obtenerDocumentos(lista));
    } catch (error) {
      console.error("Error al cargar documentos:", error);
      mostrarError("No se pudieron cargar los documentos", "Revise su conexión a internet e intente de nuevo.");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { capturarDatos(); }, [capturarDatos]);

  // Borra el PDF de Storage solo si es nuestro y ningún otro documento tiene el mismo enlace.
  const borrarSiNadieLoUsa = useCallback(async (documento) => {
    if (!esArchivoPropio(documento.url)) return;
    if (documentos.some((d) => d.id !== documento.id && d.url === documento.url)) return;
    await borrarArchivoFirebaseUrl(documento.url);
  }, [documentos]);

  const guardarDocumento = useCallback(async (documento = null) => {
    const esEdicion = Boolean(documento);
    const elegirCopropiedad = !esEdicion && copropiedades.length > 1;

    const { value: valores } = await dialogo({
      icono: esEdicion ? "bi-pencil" : "bi-file-earmark-plus",
      titulo: esEdicion ? "Editar documento" : "Agregar documento",
      texto: esEdicion
        ? "Cambie lo que necesite y toque Guardar."
        : "Escriba el título y la descripción, suba el PDF o pegue su enlace y toque Guardar.",
      amplio: true,
      contenido: formularioHTML(documento || {}, copropiedades),
      confirmar: "Guardar",
      iconoConfirmar: "bi-check-lg",
      cancelar: "Cancelar",
      focusConfirm: false,
      didOpen: (popup) => {
        const input = popup.querySelector("#docArchivo");
        input.addEventListener("change", () => {
          const archivo = input.files?.[0];
          if (!archivo) return;
          popup.querySelector("#docArchivoNombre").textContent = archivo.name;
          popup.querySelector("#docArchivoCaja").classList.add("listo");
        });
      },
      preConfirm: () => leerFormulario(esEdicion, elegirCopropiedad),
    });

    if (!valores) return;

    mostrarEspera(
      valores.archivo ? "Subiendo el PDF" : "Guardando el documento",
      valores.archivo ? "Esto puede tardar unos segundos según el tamaño del archivo. No cierre la página." : "Espere un momento por favor."
    );

    try {
      const url = valores.archivo ? await subirPdf(valores.archivo) : valores.enlace;
      const registro = { titulo: valores.titulo, descripcion: valores.descripcion, url };

      if (esEdicion) {
        await actualizarRegistro(rutaDocumento(documento.nit, documento.id), registro);
        if (url !== documento.url) await borrarSiNadieLoUsa(documento);
      } else {
        await crearRegistro(rutaColeccion(valores.nit || copropiedades[0]?.nit), registro);
      }

      await dialogo({
        icono: "bi-check-circle",
        tono: "verde",
        titulo: esEdicion ? "Documento actualizado" : "Documento agregado",
        texto: "Ya lo pueden ver en la app y en la web.",
        timer: 2200,
        showConfirmButton: false,
      });
      capturarDatos();
    } catch (error) {
      console.error("Error al guardar documento:", error);
      mostrarError("No se pudo guardar el documento", "Revise su conexión a internet e intente de nuevo.");
    }
  }, [capturarDatos, copropiedades, borrarSiNadieLoUsa]);

  const eliminarDocumento = useCallback(async (documento) => {
    const { isConfirmed } = await dialogo({
      icono: "bi-trash",
      tono: "rojo",
      titulo: "¿Eliminar documento?",
      texto: `<strong>${escapar(documento.titulo)}</strong><br>Se borra para siempre, también en la app. No se puede deshacer.`,
      confirmar: "Sí, eliminar",
      iconoConfirmar: "bi-trash",
      cancelar: "Cancelar",
    });
    if (!isConfirmed) return;

    mostrarEspera("Eliminando el documento", "Espere un momento por favor.");
    try {
      await eliminarRegistro(rutaDocumento(documento.nit, documento.id));
      await borrarSiNadieLoUsa(documento);
      await dialogo({ icono: "bi-check-circle", tono: "verde", titulo: "Documento eliminado", timer: 1800, showConfirmButton: false });
      capturarDatos();
    } catch (error) {
      console.error("Error al eliminar documento:", error);
      mostrarError("No se pudo eliminar el documento", "Revise su conexión a internet e intente de nuevo.");
    }
  }, [capturarDatos, borrarSiNadieLoUsa]);

  const abrirOpciones = useCallback(async (documento) => {
    if (!gestionar) return verPdf(documento);
    const opcion = await menuOpciones({
      icono: "bi-file-earmark-pdf",
      titulo: escapar(documento.titulo),
      texto: "¿Qué desea hacer con este documento?",
      opciones: [
        { clave: "ver", icono: "bi-eye", nombre: "Ver PDF", detalle: "Abre el archivo para leerlo o descargarlo." },
        { clave: "editar", icono: "bi-pencil", nombre: "Editar", detalle: "Cambia el título, la descripción, el PDF o el enlace." },
        { clave: "eliminar", icono: "bi-trash", nombre: "Eliminar", detalle: "Borra el documento para siempre. No se puede deshacer." },
      ],
    });
    if (opcion === "ver") verPdf(documento);
    if (opcion === "editar") guardarDocumento(documento);
    if (opcion === "eliminar") eliminarDocumento(documento);
  }, [gestionar, guardarDocumento, eliminarDocumento]);

  if (cargando) return <AnimacionCarga />;

  const columnas = esSuper ? COLUMNAS_SUPER : COLUMNAS;

  const filas = documentos.map((documento) => ({
    ...documento,
    archivo: documento.url ? "Adjunto" : "Sin archivo",
  }));

  const conteo = filas.reduce((acc, fila) => {
    acc[fila.archivo] = (acc[fila.archivo] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      <TablaGenerica
        datos={filas}
        titulos={columnas.map((c) => c.label.toUpperCase())}
        campos={columnas.map((c) => c.key)}
        elementosPorPagina={3000}
        columnaOrdenInicial={0}
        direccionOrdenInicial="asc"
        contenidoExtra={
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "12px 0", alignItems: "center" }}>
            {Object.entries(conteo).map(([etiqueta, cantidad]) => (
              <div key={etiqueta} style={{ padding: "6px 14px", borderRadius: 20, background: "#f0f0f0", fontWeight: "bold", fontSize: 14 }}>
                {etiqueta}: {cantidad}
              </div>
            ))}
            <div style={{ marginLeft: "auto", display: "flex", gap: 0 }}>
              <button onClick={capturarDatos} title="Refrescar"><i className="bi bi-arrow-clockwise" /></button>
              <CSVLink data={filas} headers={columnas} filename="listado_documentos.csv" separator=";" style={estiloCSV} title="Exportar"><i className="bi bi-download" /></CSVLink>
              {gestionar && (
                <button onClick={() => guardarDocumento()} title="Agregar documento"><i className="bi bi-plus-lg" /> Agregar</button>
              )}
            </div>
          </div>
        }
        botones={gestionar ? botonOpciones(abrirOpciones) : [{ texto: "Ver PDF", icono: "bi-file-earmark-pdf", accion: verPdf }]}
      />

      <BotonSOS />
    </>
  );
});

export default ListadoDocumentos;
